/**
 * 檔案用途區塊
 * @module multi-chain-signature.helper
 * @purpose 依據 CAIP-10 地址的 chain namespace 分派對應簽名流程，並整合 EVM 與 Bitcoin 的訊息簽名處理與錯誤彙整。
 * @external https://innotech.atlassian.net/browse/IN-111892
 */

import { modal as appKitModal } from '@reown/appkit/react';
import { ethers } from 'ethers';
import { parseNamespaceFromCaipAddress } from '@modules/walletconnect/config/token-network-adapter-mapping';

/**
 * 宣告內容用途說明與單號關聯
 * @description 定義跨鏈簽名成功後回傳的標準資料結構。
 * @purpose 統一上層流程可取得的 signature、原始 message 與 caipAddress 欄位。
 */
export interface SignMessageResult {
  signature: string;
  message: string;
  caipAddress: string;
}

/**
 * 宣告內容用途說明與單號關聯
 * @description 透過 EIP-1193 provider 與 ethers signer 執行 EVM 鏈訊息簽名。
 * @purpose 產出可供後端驗證的 EVM signature 與原始請求內容。
 * @external https://innotech.atlassian.net/browse/IN-111892
 */
async function signMessageEVM(message: string, caipAddress: string): Promise<SignMessageResult> {
  const appKitInstance = appKitModal;
  if (!appKitInstance) {
    throw new Error('AppKit modal 尚未初始化');
  }

  const walletProviderUnknown = await appKitInstance.getWalletProvider();
  const browserProvider = new ethers.BrowserProvider(walletProviderUnknown as ethers.Eip1193Provider);
  const signer = await browserProvider.getSigner();
  const signature = await signer.signMessage(message);

  return {
    signature,
    message,
    caipAddress,
  };
}

/**
 * 宣告內容用途說明與單號關聯
 * @description 定義 Bitcoin provider 需具備的 request 介面。
 * @purpose 以最小能力約束與 Bitcoin 簽名 RPC 方法互動。
 */
type BitcoinWalletProvider = { request: (params: unknown) => Promise<unknown> };

/**
 * 宣告內容用途說明與單號關聯
 * @description 從 CAIP-10 地址拆出 Bitcoin 地址段。
 * @purpose 提供 bitcoin_signMessage 與 btc_signMessage 所需 address 參數。
 * @external https://innotech.atlassian.net/browse/IN-111892
 */
function extractBitcoinAddressFromCaip(caipAddress: string): string {
  const parts = caipAddress.split(':');
  return parts[parts.length - 1] ?? '';
}

/**
 * 宣告內容用途說明與單號關聯
 * @description 取得並驗證 AppKit 回傳之 wallet provider 是否可用於 Bitcoin request。
 * @purpose 在進入簽名前先確保 provider 具備 request 方法與可呼叫性。
 * @external https://innotech.atlassian.net/browse/IN-111892
 */
async function getBitcoinWalletProvider(): Promise<BitcoinWalletProvider> {
  const appKitInstance = appKitModal;
  if (!appKitInstance) {
    throw new Error('AppKit modal 尚未初始化');
  }

  const walletProvider = await appKitInstance.getWalletProvider();
  if (!walletProvider || typeof walletProvider !== 'object') {
    throw new Error('無法取得 Bitcoin wallet provider');
  }

  if (!('request' in walletProvider) || typeof walletProvider.request !== 'function') {
    throw new Error('Bitcoin wallet provider 不支援 request 方法');
  }

  return walletProvider as BitcoinWalletProvider;
}

/**
 * 宣告內容用途說明與單號關聯
 * @description 使用 bitcoin_signMessage 方法嘗試 Bitcoin 簽名，並回傳成功或錯誤資訊。
 * @purpose 作為 Bitcoin 多方法嘗試流程的第一優先簽名策略。
 * @external https://innotech.atlassian.net/browse/IN-111892
 */
async function tryBitcoinSignWithBitcoinMethod(
  provider: BitcoinWalletProvider,
  message: string,
  bitcoinAddress: string,
): Promise<{ signature: string | null; error: string | null }> {
  try {
    const signature = (await provider.request({
      method: 'bitcoin_signMessage',
      params: {
        message,
        address: bitcoinAddress,
      },
    })) as string;

    return { signature: signature || null, error: null };
  } catch (error) {
    const errorMessage = error instanceof Error ? error.message : String(error);
    return { signature: null, error: `bitcoin_signMessage 失敗：${errorMessage}` };
  }
}

/**
 * 宣告內容用途說明與單號關聯
 * @description 使用 btc_signMessage 方法嘗試 Bitcoin 簽名，並回傳成功或錯誤資訊。
 * @purpose 在第一方法失敗時提供替代 RPC 呼叫路徑。
 * @external https://innotech.atlassian.net/browse/IN-111892
 */
async function tryBitcoinSignWithBtcMethod(
  provider: BitcoinWalletProvider,
  message: string,
  bitcoinAddress: string,
): Promise<{ signature: string | null; error: string | null }> {
  try {
    const signature = (await provider.request({
      method: 'btc_signMessage',
      params: {
        message,
        address: bitcoinAddress,
      },
    })) as string;

    return { signature: signature || null, error: null };
  } catch (error) {
    const errorMessage = error instanceof Error ? error.message : String(error);
    return { signature: null, error: `btc_signMessage 失敗：${errorMessage}` };
  }
}

/**
 * 宣告內容用途說明與單號關聯
 * @description 使用通用 signMessage 方法嘗試 Bitcoin 簽名，並回傳成功或錯誤資訊。
 * @purpose 提供最後回退策略以提高跨錢包相容性。
 * @external https://innotech.atlassian.net/browse/IN-111892
 */
async function tryBitcoinSignWithGenericMethod(
  provider: BitcoinWalletProvider,
  message: string,
  caipAddress: string,
): Promise<{ signature: string | null; error: string | null }> {
  try {
    const signature = (await provider.request({
      method: 'signMessage',
      params: {
        message,
        address: caipAddress,
      },
    })) as string;

    return { signature: signature || null, error: null };
  } catch (error) {
    const errorMessage = error instanceof Error ? error.message : String(error);
    return { signature: null, error: `signMessage 失敗：${errorMessage}` };
  }
}

/**
 * 宣告內容用途說明與單號關聯
 * @description 定義 Bitcoin 多方法簽名流程中的中繼結果型別。
 * @purpose 讓流程可同時攜帶第一個成功 signature 或累積失敗原因。
 */
interface BitcoinSignMethodResult {
  signature: string | null;
  errors: string[];
}

/**
 * 宣告內容用途說明與單號關聯
 * @description 將單次方法嘗試結果合併到目前累積狀態。
 * @purpose 發現成功簽名即短路回傳，否則持續累積錯誤訊息。
 * @external https://innotech.atlassian.net/browse/IN-111892
 */
function appendResult(
  currentResult: BitcoinSignMethodResult,
  nextResult: { signature: string | null; error: string | null },
): BitcoinSignMethodResult {
  if (nextResult.signature) {
    return { signature: nextResult.signature, errors: [] };
  }
  const errors = nextResult.error ? [...currentResult.errors, nextResult.error] : currentResult.errors;
  return { signature: null, errors };
}

/**
 * 宣告內容用途說明與單號關聯
 * @description 依序嘗試三種 Bitcoin 簽名 RPC 方法並彙整結果。
 * @purpose 提升不同錢包實作下的簽名成功率，並保留完整失敗軌跡。
 * @external https://innotech.atlassian.net/browse/IN-111892
 */
async function tryAllBitcoinSignMethods(
  provider: BitcoinWalletProvider,
  message: string,
  caipAddress: string,
): Promise<{ signature: string | null; errors: string[] }> {
  const errors: string[] = [];
  const bitcoinAddress = extractBitcoinAddressFromCaip(caipAddress);

  let result: BitcoinSignMethodResult = { signature: null, errors };

  // 嘗試方法 1: bitcoin_signMessage
  const result1 = await tryBitcoinSignWithBitcoinMethod(provider, message, bitcoinAddress);
  result = appendResult(result, result1);
  if (result.signature) {
    return result;
  }

  // 嘗試方法 2: btc_signMessage
  const result2 = await tryBitcoinSignWithBtcMethod(provider, message, bitcoinAddress);
  result = appendResult(result, result2);
  if (result.signature) {
    return result;
  }

  // 嘗試方法 3: signMessage
  const result3 = await tryBitcoinSignWithGenericMethod(provider, message, caipAddress);
  result = appendResult(result, result3);

  return result;
}

/**
 * 宣告內容用途說明與單號關聯
 * @description 將多方法嘗試失敗訊息格式化為可讀錯誤內容。
 * @purpose 提供呼叫端與除錯流程可直接使用的整體失敗原因。
 * @external https://innotech.atlassian.net/browse/IN-111892
 */
function createBitcoinSignErrorMessage(errors: string[]): string {
  const errorDetails = errors.length > 0 ? `嘗試的方法：\n${errors.join('\n')}` : '未返回任何錯誤訊息';
  return `Bitcoin 簽名失敗：錢包不支援 Bitcoin message signing。${errorDetails}`;
}

/**
 * 宣告內容用途說明與單號關聯
 * @description 執行 Bitcoin 簽名主流程，取得 provider、嘗試多種方法並在失敗時拋錯。
 * @purpose 封裝 Bitcoin 鏈簽名行為，回傳與 EVM 相同格式的結果物件。
 * @external https://innotech.atlassian.net/browse/IN-111892
 */
async function signMessageBitcoin(message: string, caipAddress: string): Promise<SignMessageResult> {
  const provider = await getBitcoinWalletProvider();
  const { signature, errors } = await tryAllBitcoinSignMethods(provider, message, caipAddress);

  if (!signature) {
    throw new Error(createBitcoinSignErrorMessage(errors));
  }

  return {
    signature,
    message,
    caipAddress,
  };
}

// function signMessageTRON() {
//   // TODO: 實作 TRON 簽名
//   // 需要：
//   // 1. TRON 專用的 wallet adapter（不是 WagmiAdapter）
//   // 2. TronWeb SDK
//   // 3. 後端支援 TRON 簽名驗證
//   throw new Error('TRON 鏈簽名尚未實作，需要額外的 adapter 和 SDK');
// }

/**
 * 宣告內容用途說明與單號關聯
 * @description 解析 CAIP-10 namespace 並分派到對應鏈的簽名實作。
 * @purpose 對外提供單一入口，統一多鏈簽名呼叫方式與錯誤處理邊界。
 * @external https://innotech.atlassian.net/browse/IN-111892
 */
export async function signMessageByChain(message: string, caipAddress: string): Promise<SignMessageResult> {
  const namespace = parseNamespaceFromCaipAddress(caipAddress);

  switch (namespace) {
    case 'eip155':
      // EVM 鏈（Ethereum、Polygon、BSC 等）
      return signMessageEVM(message, caipAddress);

    case 'bip122':
      // Bitcoin
      return signMessageBitcoin(message, caipAddress);

    case 'tron':
    default:
      throw new Error(`不支援的 chain namespace: ${namespace || 'unknown'}`);
  }
}

/**
 * === llm 分析紀錄區 ===
 * @llm-review-submitted-at 2026-06-14T07:17:26.715Z
 * @llm-review-model gpt-5.3-codex
 * @llm-review-note Reformatted annotations into required three-section layout, replaced/added JSDoc blocks for declarations with @description/@purpose, mapped @external links to provided IN-111892 ticket where applicable, omitted @external when no ticket, and preserved runtime logic unchanged.
 */
